// src/pages/Clients.jsx
import { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import FormRow from '../components/FormRow';
import { useAuth } from '../context/AuthContext';
import {
  listClientsApi, createClientApi, updateClientApi, setClientStatusApi
} from '../api/http';
import { FaPen, FaSyncAlt, FaTrash, FaBuilding, FaUserTie, FaPlus } from 'react-icons/fa';

// Estilos modernos alineados con index.css
const btnPrimary = {
  background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
  border: '1px solid #111827',
  color: '#ffffff',
  fontWeight: 600,
  transition: 'all 0.15s ease',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  padding: '8px 12px'
};

const btnSecondary = {
  background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
  border: '1px solid #d1d5db',
  color: '#374151',
  fontWeight: 500,
  transition: 'all 0.15s ease',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  padding: '8px 12px'
};

/* ===== Helpers (RUT, email, phone) — calcados del Users.jsx ===== */
const onlyDigits = (s) => (s || '').replace(/\D/g, '');
function rutDV(num){let m=2,s=0;for(let i=num.length-1;i>=0;i--){s+=Number(num[i])*m;m=m===7?2:m+1}const r=11-(s%11);return r===11?'0':r===10?'K':String(r);}
function normalizeRut(raw){const clean=(raw||'').replace(/\./g,'').replace(/-/g,'').toUpperCase();if(clean.length<2)return clean;const body=String(parseInt(clean.slice(0,-1),10));const dv=clean.slice(-1);return `${body}-${dv}`;}
function formatRut(raw){const norm=normalizeRut(raw);const [b='',dv='']=norm.split('-');const withDots=b.replace(/\B(?=(\d{3})+(?!\d))/g,'.');return dv?`${withDots}-${dv}`:withDots;}
function isValidRut(raw){const norm=normalizeRut(raw);if(!/^\d{1,8}-[\dK]$/.test(norm))return false;const [n,dv]=norm.split('-');return rutDV(n)===dv.toUpperCase();}
const emailRe=/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const isValidEmail=(e)=>emailRe.test(e||'');
function digitsPhoneCL(raw){let d=onlyDigits(raw);if(d.startsWith('56')) d=d.slice(2);if(d.startsWith('0')) d=d.slice(1);if(d.startsWith('9')) d=d.slice(1);return d.slice(0,8);}
function formatPhoneCL(raw){const d=digitsPhoneCL(raw);const left=d.slice(0,4);const right=d.slice(4);return `+56 9 ${left}${right?' '+right:''}`.trimEnd();}
function normalizePhoneCL(raw){const d=digitsPhoneCL(raw);return d.length===8?`569${d}`:null;}
const isValidPhone=(p)=>!p || !!normalizePhoneCL(p);

function validate(form){
  const errs={};
  if(!form.name?.trim()) errs.name='Requerido';
  if(!isValidRut(form.rut)) errs.rut='RUT inválido';
  if(!form.responsible_name?.trim()) errs.responsible_name='Requerido';
  if(!isValidEmail(form.responsible_email)) errs.responsible_email='Email inválido';
  if(!isValidPhone(form.phone)) errs.phone='Fono inválido (+56 9 xxxx xxxx)';
  return errs;
}

/* ===== Pills ===== */
function StatusPill({ status }){
  const s=(status||'').toLowerCase();
  const label={active:'Activo',inactive:'Inactivo',deleted:'Eliminado'}[s]||status;
  const base={display:'inline-block',padding:'2px 10px',borderRadius:999,fontSize:12,fontWeight:700};
  const color=s==='inactive'?{background:'#f59e0b',color:'#111'}
           :s==='deleted'?{background:'#ef4444',color:'#fff'}
           :{background:'#10b981',color:'#fff'};
  return <span style={{...base,...color}}>{label}</span>;
}

/* ===== Banner ===== */
function Banner({kind='info',children,onClose}) {
  if(!children) return null;
  const s=kind==='error'?{bg:'#fee2e2',fg:'#991b1b',br:'#fecaca'}:{bg:'#dcfce7',fg:'#065f46',br:'#bbf7d0'};
  return (
    <div style={{background:s.bg,color:s.fg,border:`1px solid ${s.br}`,padding:10,borderRadius:8,marginTop:12,display:'flex',justifyContent:'space-between',gap:12}}>
      <div style={{overflowWrap:'anywhere'}}>{children}</div>
      <button className="btn" onClick={onClose}>OK</button>
    </div>
  );
}

/* ===== Modal Crear/Editar Cliente ===== */
function ClientFormModal({
  open,title,editingId,form,touched,errors,serverErrs,
  setField,markTouched,loading,onClose,onSubmit,showErr
}){
  useEffect(()=>{ if(!open) return; const prev=document.body.style.overflow; document.body.style.overflow='hidden'; return ()=>{document.body.style.overflow=prev}; },[open]);
  if(!open) return null;
  const fieldStyle={width:'100%',minWidth:0};

  return (
    <div role="dialog" aria-modal="true"
      onKeyDown={(e)=>e.key==='Escape'&&onClose()}
      onMouseDown={(e)=>{if(e.target===e.currentTarget) onClose();}}
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:12}}
      tabIndex={-1}
    >
      <div style={{background:'#f3f4f6',color:'#111',padding:18,borderRadius:12,width:'min(96vw,560px)',boxSizing:'border-box',maxHeight:'90vh',overflowY:'auto',overflowX:'hidden'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <h4 style={{margin:0}}>{title}</h4>
          <button className="btn" onClick={onClose}>Cerrar</button>
        </div>

        <form onSubmit={onSubmit} noValidate>
          <FormRow label="Nombre del cliente">
            <input autoFocus style={fieldStyle}
              value={form.name} onChange={e=>setField('name',e.target.value)} onBlur={()=>markTouched('name')}
            />
            {showErr('name') && <small className="error">{showErr('name')}</small>}
          </FormRow>
          <FormRow label="RUT">
            <input style={fieldStyle}
              value={form.rut} onChange={e=>setField('rut',e.target.value)} onBlur={()=>markTouched('rut')} placeholder="76.123.456-7" maxLength={12}
            />
            {showErr('rut') && <small className="error">{showErr('rut')}</small>}
          </FormRow>
          <FormRow label="Responsable">
            <input style={fieldStyle}
              value={form.responsible_name} onChange={e=>setField('responsible_name',e.target.value)} onBlur={()=>markTouched('responsible_name')}
            />
            {showErr('responsible_name') && <small className="error">{showErr('responsible_name')}</small>}
          </FormRow>
          <FormRow label="Correo del responsable">
            <input type="email" style={fieldStyle}
              value={form.responsible_email} onChange={e=>setField('responsible_email',e.target.value)} onBlur={()=>markTouched('responsible_email')}
            />
            {showErr('responsible_email') && <small className="error">{showErr('responsible_email')}</small>}
          </FormRow>
          <FormRow label="Fono">
            <input style={fieldStyle}
              value={form.phone} onChange={e=>setField('phone',e.target.value)} onBlur={()=>markTouched('phone')} placeholder="+56 9 1234 5678" maxLength={16}
            />
            {showErr('phone') && <small className="error">{showErr('phone')}</small>}
          </FormRow>

          <div style={{display:'flex',gap:8,justifyContent:'flex-end',flexWrap:'wrap'}}>
            <button className="btn" disabled={loading}>{loading?'Guardando…':editingId?'Guardar':'Crear'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ===== Página ===== */
/* ========= Tabla de Clientes con paginación ========= */
function ClientsTable({ rows, isAdmin, onEdit, onToggleStatus, onDelete, page, pageSize, totalCount, onPageChange }) {
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIdx = (page - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalCount);

  return (
    <div className="data-table-container">
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th className="col-date" style={{ width: '60px', textAlign: 'center' }}>ID</th>
              <th className="col-project" style={{ width: '20%' }}>Cliente</th>
              <th className="col-member" style={{ width: '12%' }}>RUT</th>
              <th className="col-member" style={{ width: '18%' }}>Responsable</th>
              <th className="col-member" style={{ width: '20%' }}>Email</th>
              <th className="col-hours" style={{ width: '12%' }}>Fono</th>
              <th className="col-rate" style={{ width: '10%', textAlign: 'center' }}>Estado</th>
              <th className="col-actions" style={{ width: '18%', textAlign: 'center' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ opacity: r.status === 'inactive' ? 0.7 : 1 }}>
                <td className="col-date" style={{ textAlign: 'center', fontWeight: 600 }}>{r.id}</td>
                <td className="col-project" style={{ fontWeight: 600, color: '#1e293b' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <FaBuilding style={{ color: '#64748b' }} />{r.name}
                  </span>
                </td>
                <td className="col-member">{r.rut}</td>
                <td className="col-member">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <FaUserTie style={{ color: '#64748b' }} />{r.responsible_name}
                  </span>
                </td>
                <td className="col-member" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }} title={r.responsible_email}>
                  {r.responsible_email}
                </td>
                <td className="col-hours">{r.phone || '-'}</td>
                <td className="col-rate" style={{ textAlign: 'center' }}>
                  <StatusPill status={r.status} />
                </td>
                <td className="col-actions">
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {isAdmin && (
                      <>
                        <button className="btn" style={btnSecondary} title="Editar" onClick={() => onEdit(r)}>
                          <FaPen /><span>Editar</span>
                        </button>
                        <button className="btn" style={btnSecondary} title={r.status === 'active' ? 'Desactivar' : 'Activar'} onClick={() => onToggleStatus(r)}>
                          <FaSyncAlt /><span>{r.status === 'active' ? 'Desact.' : 'Activar'}</span>
                        </button>
                        <button className="btn" style={btnSecondary} title="Eliminar" onClick={() => onDelete(r)}>
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="8" style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>Sin clientes</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderTop: '1px solid #e2e8f0',
          background: '#f8fafc',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Mostrando {startIdx + 1}-{endIdx} de {totalCount} clientes
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn"
              style={{
                ...btnSecondary,
                opacity: page === 1 ? 0.5 : 1,
                cursor: page === 1 ? 'not-allowed' : 'pointer'
              }}
              onClick={() => page > 1 && onPageChange(page - 1)}
              disabled={page === 1}
            >
              ← Anterior
            </button>
            <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 500, padding: '0 8px' }}>
              Página {page} de {totalPages}
            </span>
            <button
              className="btn"
              style={{
                ...btnSecondary,
                opacity: page === totalPages ? 0.5 : 1,
                cursor: page === totalPages ? 'not-allowed' : 'pointer'
              }}
              onClick={() => page < totalPages && onPageChange(page + 1)}
              disabled={page === totalPages}
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Clients(){
  const { token, user, hasRole } = useAuth();
  const isAdmin = hasRole ? hasRole('admin') : user?.role === 'admin';

  const [rows,setRows] = useState([]);
  const [q,setQ] = useState('');

  // paginación
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // form state
  const [form,setForm] = useState({ name:'', rut:'', responsible_name:'', responsible_email:'', phone:'' });
  const [touched,setTouched] = useState({});
  const [dirty,setDirty] = useState({});
  const [submitted,setSubmitted] = useState(false);
  const [serverErrs,setServerErrs] = useState({});
  const [loading,setLoading] = useState(false);
  const [editingId,setEditingId] = useState(null);
  const [formModalOpen,setFormModalOpen] = useState(false);
  const [banner,setBanner] = useState({kind:'info',msg:''});

  const refresh = async ()=>{
    try{
      const data = await listClientsApi(token);
      setRows(Array.isArray(data)?data:[]);
    }catch(err){
      setBanner({kind:'error', msg: err?.payload?.error || err?.message || 'No se pudieron cargar clientes'});
    }
  };
  useEffect(()=>{ refresh(); /* eslint-disable-next-line */ },[]);

  // filtro
  const filtered = useMemo(()=>{
    const s=q.trim().toLowerCase();
    const base=(rows||[]).filter(r => (r?.status ?? '').toLowerCase() !== 'deleted' && r?.deleted_at == null);
    if(!s) return base;
    return base.filter(r => [r.id, r.name, r.rut, r.responsible_name, r.responsible_email, r.phone, r.status]
      .map(x => (x??'').toString().toLowerCase()).some(v => v.includes(s)));
  },[rows,q]);

  // resetear página cuando cambien los filtros
  useEffect(() => {
    setPage(1);
  }, [q]);

  // datos paginados
  const paginatedData = useMemo(() => {
    const startIdx = (page - 1) * pageSize;
    return filtered.slice(startIdx, startIdx + pageSize);
  }, [filtered, page, pageSize]);

  // helpers form
  const setField = (k,v)=>{
    if(k==='rut') v = formatRut(v);
    if(k==='phone') v = formatPhoneCL(v);
    setForm(s=>({...s,[k]:v}));
    setDirty(d=>({...d,[k]:true}));
    setServerErrs(s=>({...s,[k]:undefined}));
  };
  const markTouched = (k)=> setTouched(t=>({...t,[k]:true}));
  const errors = useMemo(()=> validate(form), [form]);

  // mostrar error solo si submitted || (touched && (dirty || valor no vacío))
  const showErr = (k)=>{
    const val = (form[k]??'').toString().trim();
    const canShow = submitted || (touched[k] && (dirty[k] || val !== ''));
    return canShow ? (errors[k] || serverErrs[k]) : null;
  };

  const openCreate = ()=>{
    setEditingId(null);
    setForm({ name:'', rut:'', responsible_name:'', responsible_email:'', phone:'' });
    setTouched({}); setDirty({}); setSubmitted(false); setServerErrs({});
    setFormModalOpen(true);
  };
  const startEdit = (row)=>{
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      rut: formatRut(row.rut || ''),
      responsible_name: row.responsible_name || '',
      responsible_email: row.responsible_email || '',
      phone: row.phone ? formatPhoneCL(row.phone) : ''
    });
    setTouched({}); setDirty({}); setSubmitted(false); setServerErrs({});
    setFormModalOpen(true);
  };
  const cancelEdit = ()=>{
    setEditingId(null);
    setForm({ name:'', rut:'', responsible_name:'', responsible_email:'', phone:'' });
    setTouched({}); setDirty({}); setSubmitted(false); setServerErrs({});
    setFormModalOpen(false);
  };

  const submit = async (e)=>{
    e.preventDefault();
    setSubmitted(true);
    setTouched({ name:true, rut:true, responsible_name:true, responsible_email:true, phone:true });
    if (Object.keys(errors).length) return;

    const payload = {
      name: form.name.trim(),
      rut: normalizeRut(form.rut),
      responsible_name: form.responsible_name.trim(),
      responsible_email: form.responsible_email.trim(),
      ...(form.phone ? { phone: normalizePhoneCL(form.phone) } : {})
    };

    try{
      setLoading(true);
      if(editingId){
        await updateClientApi(editingId, payload, token);
        setBanner({kind:'info', msg:'Cliente actualizado'});
      }else{
        await createClientApi(payload, token);
        setBanner({kind:'info', msg:'Cliente creado'});
      }
      cancelEdit();
      await refresh();
    }catch(err){
      if(err.status===409){
        const msg=(err.message || err?.payload?.error || '').toLowerCase();
        const newErrs={};
        if(msg.includes('rut')) newErrs.rut='RUT ya registrado';
        setServerErrs(s=>({ ...s, ...newErrs })); setTouched(s=>({ ...s, rut:true }));
      }else{
        setBanner({kind:'error', msg: err?.payload?.error || err?.message || 'Error al guardar'});
      }
    }finally{
      setLoading(false);
    }
  };

  const toggleStatus = async (row)=>{
    const next = row.status==='active' ? 'inactive' : 'active';
    try{
      setLoading(true);
      await setClientStatusApi(row.id,next,token);
      setBanner({kind:'info', msg: next==='active'?'Cliente activado':'Cliente desactivado'});
      await refresh();
    }catch(err){
      setBanner({kind:'error', msg: err?.payload?.error || err?.message || 'No se pudo cambiar estado'});
    }finally{ setLoading(false); }
  };
  const deleteSoft = async (row)=>{
    if(!window.confirm('¿Eliminar cliente (soft)?')) return;
    try{
      setLoading(true);
      await setClientStatusApi(row.id,'deleted',token);
      setBanner({kind:'info', msg:'Cliente eliminado (soft)'});
      await refresh();
    }catch(err){
      setBanner({kind:'error', msg: err?.payload?.error || err?.message || 'No se pudo eliminar'});
    }finally{ setLoading(false); }
  };

  return (
    <Layout title="Clientes" wide>
      <div className="container-tw">
        {/* Toolbar */}
        <div className="toolbar">
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Listado de Clientes</h3>
          <div className="toolbar-actions">
            {isAdmin && (
              <button className="btn" onClick={openCreate} style={btnPrimary} title="Crear cliente">
                <FaPlus /><span>Nuevo Cliente</span>
              </button>
            )}
          </div>
        </div>

        <Banner kind={banner.kind} onClose={() => setBanner({ kind: 'info', msg: '' })}>{banner.msg}</Banner>

        {/* Filters Card */}
        <div className="filters-card">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
            <input
              type="search"
              placeholder="Buscar nombre, RUT o responsable..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                flex: '1 1 320px',
                minWidth: '200px',
                fontSize: '14px'
              }}
            />
            {q && (
              <button className="btn" style={btnSecondary} onClick={() => setQ('')}>
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Tabla */}
        <div>
          <ClientsTable
            rows={paginatedData}
            isAdmin={isAdmin}
            onEdit={startEdit}
            onToggleStatus={toggleStatus}
            onDelete={deleteSoft}
            page={page}
            pageSize={pageSize}
            totalCount={filtered.length}
            onPageChange={setPage}
          />
        </div>
      </div>

      <ClientFormModal
        open={formModalOpen}
        title={editingId ? `Editar cliente #${editingId}` : 'Crear cliente'}
        editingId={editingId}
        form={form}
        touched={touched}
        errors={errors}
        serverErrs={serverErrs}
        setField={setField}
        markTouched={markTouched}
        loading={loading}
        onClose={cancelEdit}
        onSubmit={submit}
        showErr={(k)=> (submitted || (touched[k] && ((dirty[k]) || (form[k]??'').toString().trim()!==''))) ? (errors[k] || serverErrs[k]) : null}
      />
    </Layout>
  );
}
